import {messages} from '@smartmixin/i18n';
import moment from 'moment-timezone';
import type {MessageDescriptor} from 'react-intl';
import type {WeatherFamily} from '../../theme/colorRoles';
import * as Dates from '../Dates';
import * as Units from '../Units';

/**
 * The all-time records card of the "Records & climate" report (FE #97), shared
 * by both apps. Core owns the selection rules and the formatted strings; each
 * app only lays them out (mobile: one row per family; webapp: a table).
 *
 * Payload: `GET stations/{id}/records` → `all_time` (backend
 * apps/measures/records/profile.py `_ALL_TIME_RULES`) and `live_records`.
 */

/** One `all_time` entry. `at` (epoch seconds) exists for T, FXY and T_*_CLIM. */
export type RecordEntry = {
  value: number;
  date?: string | null;
  at?: number | null;
};

/** One `live_records` metric. */
export type LiveRecordSide = {
  min?: number | null;
  min_at?: number | null;
  min_date?: string | null;
  max?: number | null;
  max_at?: number | null;
  max_date?: string | null;
};

/** The subset of the records payload this module reads. */
export type StationRecords =
  | {
      all_time?: Record<string, RecordEntry | undefined> | null;
      live_records?: Record<string, LiveRecordSide | undefined> | null;
      /**
       * Who wrote the `*_CLIM` temperatures: `'meteo_france'` on a station
       * matched to Météo-France (its published daily Tn/Tx), null anywhere
       * else, where they are our own rollup of the same hourly readings.
       */
      clim_source?: string | null;
      [key: string]: unknown;
    }
  | null
  | undefined;

export type RecordFamilyName =
  | 'temperature'
  | 'rain'
  | 'wind'
  | 'pressure'
  | 'humidity';

/** `min`/`max`: one side of a pair. `one`: a family with a single record. */
export type RecordSide = 'min' | 'max' | 'one';

type DataType = 'T' | 'RR' | 'FXY' | 'SLP' | 'U';

/** Selection only: which entry wins, before any formatting. */
export type RawRecordCell = {
  side: RecordSide;
  /** The table column it belongs to (webapp); a single record has one too. */
  column: 'min' | 'max';
  dataType: DataType;
  /** The record's full name: "Record low", "Minimum pressure"… */
  label: MessageDescriptor;
  entry: RecordEntry;
  /** The weather service's climatological value beat the midnight one. */
  official: boolean;
  /** The midnight-day value an official one replaced. */
  replaced?: RecordEntry;
};

export type RawRecordFamily = {
  family: RecordFamilyName;
  cells: RawRecordCell[];
  /** The wettest climatological day (06–06 UTC), only on a different date. */
  climRain?: RecordEntry;
};

export type RecordCell = {
  side: RecordSide;
  column: 'min' | 'max';
  label: MessageDescriptor;
  /** Converted to the user's units, e.g. "40.3". */
  value: string;
  /** e.g. "°C". */
  unit: string;
  /** "Jul 28, 1921", or "Jun 27, 2026 · 16:00" when the time is known. */
  when: string;
  /** "July 28, 1921", "June 27, 2026, 16:00" — for a screen reader. */
  spokenWhen: string;
  official: boolean;
  replaced?: RecordCell;
};

export type RecordFamily = {
  family: RecordFamilyName;
  /** The theme family both apps paint the row with. */
  weatherFamily: WeatherFamily;
  /** The family's name: "Temperature", "Pressure"… */
  label: MessageDescriptor;
  cells: RecordCell[];
  climRain?: RecordCell;
  /** Something on this row is explained by the info sheet (the ⓘ). */
  hasInfo: boolean;
};

export type AllTimeViewOptions = {
  units: Units.Units;
  /** The station's IANA timezone: a record's time is local to the station. */
  timezone?: string | null;
  dateTimeConfig: Dates.DateTimeConfig;
};

const FAMILY_META: Record<
  RecordFamilyName,
  {weatherFamily: WeatherFamily; label: MessageDescriptor}
> = {
  temperature: {
    weatherFamily: 'temperature',
    label: messages.metrics.temperature,
  },
  rain: {weatherFamily: 'rain', label: messages.metrics.rain},
  wind: {weatherFamily: 'wind', label: messages.metrics.wind},
  // The webapp's getMetricFamily mapping: pressure is an overview metric,
  // humidity a water one.
  pressure: {weatherFamily: 'overview', label: messages.metrics.pressure},
  humidity: {weatherFamily: 'rain', label: messages.metrics.humidity},
};

const isValue = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const fromAllTime = (
  records: StationRecords,
  key: string,
): RecordEntry | undefined => {
  const entry = records?.all_time?.[key];
  return entry && isValue(entry.value) ? entry : undefined;
};

/**
 * `live_records` stands in, per key, when the daily profile lacks it: a
 * station with a live snapshot but no climate profile yet. It never carries a
 * climatological value, and daily rain has no live equivalent.
 */
const LIVE_FALLBACK: Record<string, [string, 'min' | 'max']> = {
  T_MIN: ['T', 'min'],
  T_MAX: ['T', 'max'],
  FXY_MAX: ['FXY', 'max'],
  SLP_MIN: ['SLP', 'min'],
  SLP_MAX: ['SLP', 'max'],
  U_MIN: ['U', 'min'],
};

const fromLive = (
  records: StationRecords,
  key: string,
): RecordEntry | undefined => {
  const [metric, side] = LIVE_FALLBACK[key];
  const live = records?.live_records?.[metric];
  const value = live?.[side];
  if (!isValue(value)) {
    return undefined;
  }
  return {
    value,
    date: live?.[`${side}_date`] ?? undefined,
    at: live?.[`${side}_at`] ?? undefined,
  };
};

const single = (records: StationRecords, key: string) =>
  fromAllTime(records, key) ?? fromLive(records, key);

/**
 * Only Météo-France's published dailies are official. Anywhere else `*_CLIM`
 * is the same hourly readings over another window: it cannot hold a more
 * extreme reading, so when it "wins" it is a value QC kept in one series and
 * not the other (57.2 °C against 41.0 on a production station).
 */
const hasOfficialDailies = (records: StationRecords) =>
  records?.clim_source === 'meteo_france';

/**
 * A temperature side: on a station with official dailies, the more extreme
 * of the midnight-day series and the climatological one (`*_CLIM`, the
 * weather service's published daily high/low) wins. A tie goes to the
 * midnight entry — no evidence of a better source — and so does a lone
 * climatological value, which is shown untagged. Without official dailies,
 * the midnight entry alone.
 */
const pickTemperature = (
  records: StationRecords,
  key: 'T_MIN' | 'T_MAX',
): Pick<RawRecordCell, 'entry' | 'official' | 'replaced'> | undefined => {
  const mid = single(records, key);
  const clim = hasOfficialDailies(records)
    ? fromAllTime(records, `${key}_CLIM`)
    : undefined;
  if (!mid) {
    return clim ? {entry: clim, official: false} : undefined;
  }
  if (!clim) {
    return {entry: mid, official: false};
  }
  const beats =
    key === 'T_MAX' ? clim.value > mid.value : clim.value < mid.value;
  return beats
    ? {entry: clim, official: true, replaced: mid}
    : {entry: mid, official: false};
};

/**
 * The selection rules, without formatting: which records exist and which
 * entry each one shows. Families in a fixed order; a family with no record is
 * omitted. `U_MAX` is never read: humidity sensors saturate at 100 %.
 */
export const pickAllTime = (records: StationRecords): RawRecordFamily[] => {
  const families: RawRecordFamily[] = [];

  const temperature: RawRecordCell[] = [];
  const low = pickTemperature(records, 'T_MIN');
  if (low) {
    temperature.push({
      side: 'min',
      column: 'min',
      dataType: 'T',
      label: messages.reports.recordLow,
      ...low,
    });
  }
  const high = pickTemperature(records, 'T_MAX');
  if (high) {
    temperature.push({
      side: 'max',
      column: 'max',
      dataType: 'T',
      label: messages.reports.recordHigh,
      ...high,
    });
  }
  if (temperature.length > 0) {
    families.push({family: 'temperature', cells: temperature});
  }

  // Wettest day. A lone RR_CLIM is not shown; the climatological day only
  // when it names a different date (same day = the same storm, summed over a
  // different window).
  const rain = fromAllTime(records, 'RR');
  if (rain) {
    const clim = fromAllTime(records, 'RR_CLIM');
    families.push({
      family: 'rain',
      cells: [
        {
          side: 'one',
          column: 'max',
          dataType: 'RR',
          label: messages.reports.recordDailyRain,
          entry: rain,
          official: false,
        },
      ],
      // A dateless RR_CLIM cannot be told apart from RR's day: not shown.
      climRain: clim?.date && clim.date !== rain.date ? clim : undefined,
    });
  }

  const gust = single(records, 'FXY_MAX');
  if (gust) {
    families.push({
      family: 'wind',
      cells: [
        {
          side: 'one',
          column: 'max',
          dataType: 'FXY',
          label: messages.reports.recordWindGust,
          entry: gust,
          official: false,
        },
      ],
    });
  }

  const pressure: RawRecordCell[] = [];
  const pMin = single(records, 'SLP_MIN');
  if (pMin) {
    pressure.push({
      side: 'min',
      column: 'min',
      dataType: 'SLP',
      label: messages.reports.minPressure,
      entry: pMin,
      official: false,
    });
  }
  const pMax = single(records, 'SLP_MAX');
  if (pMax) {
    pressure.push({
      side: 'max',
      column: 'max',
      dataType: 'SLP',
      label: messages.reports.maxPressure,
      entry: pMax,
      official: false,
    });
  }
  if (pressure.length > 0) {
    families.push({family: 'pressure', cells: pressure});
  }

  const humidity = single(records, 'U_MIN');
  if (humidity) {
    families.push({
      family: 'humidity',
      cells: [
        {
          side: 'one',
          column: 'min',
          dataType: 'U',
          label: messages.reports.minHumidity,
          entry: humidity,
          official: false,
        },
      ],
    });
  }

  return families;
};

/**
 * When a record was set. With `at`, the day AND the time come from that
 * instant in the station's timezone — a `_CLIM` entry's `date` can be a day
 * off the local date of the actual reading. Without it, the date alone: no
 * time, no placeholder.
 */
export const formatRecordWhen = (
  entry: {date?: string | null; at?: number | null},
  timezone: string | null | undefined,
  dateTimeConfig: Dates.DateTimeConfig,
): {when: string; spokenWhen: string} => {
  if (isValue(entry.at)) {
    const m = timezone
      ? Dates.timestampToMoment(entry.at, timezone)
      : moment.unix(entry.at);
    return {
      when: `${m.format('ll')} · ${m.format(Dates.hourFormatter(dateTimeConfig))}`,
      // No leading zero when spoken ("7:22 PM", not "07:22 PM").
      spokenWhen: `${m.format('LL')}, ${m.format(
        dateTimeConfig.is_24_hour ? 'HH:mm' : 'h:mm A',
      )}`,
    };
  }
  if (entry.date) {
    const m = moment(entry.date, 'YYYY-MM-DD');
    return {when: m.format('ll'), spokenWhen: m.format('LL')};
  }
  return {when: '', spokenWhen: ''};
};

const toCell = (
  cell: Omit<RawRecordCell, 'replaced'> & {replaced?: RecordEntry},
  options: AllTimeViewOptions,
): RecordCell => {
  const {units, timezone, dateTimeConfig} = options;
  return {
    side: cell.side,
    column: cell.column,
    label: cell.label,
    value: Units.convertValueToString(cell.entry.value, cell.dataType, units),
    unit: Units.getUnitLabel(units, cell.dataType),
    ...formatRecordWhen(cell.entry, timezone, dateTimeConfig),
    official: cell.official,
    replaced: cell.replaced
      ? toCell(
          {...cell, entry: cell.replaced, official: false, replaced: undefined},
          options,
        )
      : undefined,
  };
};

/** The card, formatted: values in the user's units, times in the station's. */
export const allTimeView = (
  records: StationRecords,
  options: AllTimeViewOptions,
): RecordFamily[] =>
  pickAllTime(records).map((raw) => {
    const cells = raw.cells.map((c) => toCell(c, options));
    const climRain = raw.climRain
      ? toCell(
          {
            side: 'one',
            column: 'max',
            dataType: 'RR',
            label: messages.reports.recordClimatologicalDay,
            entry: raw.climRain,
            official: false,
          },
          options,
        )
      : undefined;
    return {
      family: raw.family,
      ...FAMILY_META[raw.family],
      cells,
      climRain,
      hasInfo: Boolean(climRain) || cells.some((c) => c.official),
    };
  });

type FormatMessage = (descriptor: MessageDescriptor) => string;

/**
 * What a screen reader says for one record: its name, the value with its
 * unit, when, and the official tag; a missing part is left out, never a
 * dangling ", ". A `lead` (the info sheet's "Official value" / "From hourly
 * readings") stands in for the name and the tag.
 */
export const recordCellSpokenLabel = (
  cell: RecordCell,
  formatMessage: FormatMessage,
  lead?: string,
): string =>
  [
    lead ?? formatMessage(cell.label),
    `${cell.value} ${cell.unit}`,
    cell.spokenWhen,
    lead === undefined && cell.official
      ? formatMessage(messages.reports.recordOfficialValue)
      : '',
  ]
    .filter(Boolean)
    .join(', ');

/**
 * What a screen reader says for a family row (mobile reads the row as ONE
 * element). The records use their full names — "Minimum pressure", never the
 * bare "Min." column label. Parts are joined with ", " and records with "; ".
 */
export const recordSpokenLabel = (
  family: RecordFamily,
  formatMessage: FormatMessage,
): string => {
  const parts = family.cells.map((c) =>
    recordCellSpokenLabel(c, formatMessage),
  );
  if (family.climRain) {
    parts.push(recordCellSpokenLabel(family.climRain, formatMessage));
  }
  const prefix =
    family.cells.length > 1 ? `${formatMessage(family.label)}: ` : '';
  return prefix + parts.join('; ');
};

export type RecordsInfoView = {
  /** Official values, high first, each with the value it replaced. */
  official: RecordCell[];
  climRain?: RecordCell;
};

/** What the info sheet (the ⓘ) explains for this card. */
export const recordsInfoView = (families: RecordFamily[]): RecordsInfoView => {
  const official = families
    .flatMap((f) => f.cells)
    .filter((c) => c.official && c.replaced)
    .sort((a, b) => (a.side === b.side ? 0 : a.side === 'max' ? -1 : 1));
  const climRain = families.find((f) => f.climRain)?.climRain;
  return {official, climRain};
};
